// ============================================================
// frontend/src/components/Header.tsx
// ============================================================
// Responsive navigation:
//   - Desktop: frosted top bar with horizontal tabs
//   - Mobile:  fixed bottom tab bar with icons + labels
//
// Active state highlights the current view. Detail views (client,
// session) keep their parent tab active.
// ============================================================

import { Home, Users, Calendar, Layers, Settings as SettingsIcon, Sun, Moon, HelpCircle } from 'lucide-react';
import type { ViewState } from '../App';
import { useTheme } from '../lib/theme';
import { ConsiliumLockup } from './Brand';

interface HeaderProps {
  view: ViewState;
  setView: (v: ViewState) => void;
}

// The nav only links to "no-param" views. Other views (client detail,
// session detail) require IDs and are navigated to from list rows.
type NoParamView = 'home' | 'clients' | 'sessions' | 'templates' | 'settings';

const NAV_ITEMS: { id: NoParamView; label: string; icon: typeof Home }[] = [
  { id: 'home',      label: 'Today',    icon: Home },
  { id: 'clients',   label: 'Clients',  icon: Users },
  { id: 'sessions',  label: 'Sessions', icon: Calendar },
  { id: 'templates', label: 'Packages', icon: Layers },
  { id: 'settings',  label: 'Settings', icon: SettingsIcon },
];

export function Header({ view, setView }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();

  // A detail page should keep its parent tab highlighted —
  // e.g., viewing a client keeps "Clients" active.
  const isActive = (id: string) =>
    view.name === id ||
    (id === 'clients'  && view.name === 'client') ||
    (id === 'sessions' && view.name === 'session');

  return (
    <>
      {/* Desktop top bar */}
      <header className="top-bar desktop-nav">
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '16px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div
            onClick={() => setView({ name: 'home' })}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <ConsiliumLockup height={24} />
            <div className="mono-cap">Practice</div>
          </div>

          <nav style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {NAV_ITEMS.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => setView({ name: item.id })}
                  style={{
                    background: active ? 'var(--bg-elevated)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted)',
                    padding: '9px 14px', border: 'none', borderRadius: 999,
                    fontSize: 14, fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 7,
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon size={15} />{item.label}
                </button>
              );
            })}
            <button
              onClick={toggleTheme}
              className="btn-icon" style={{ marginLeft: 6 }}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle color theme"
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button
              onClick={() => setView({ name: 'guide' })}
              className="btn-icon"
              title="How it works"
              aria-label="Open the getting-started guide"
              style={{ color: view.name === 'guide' ? 'var(--ink)' : undefined }}
            >
              <HelpCircle size={17} />
            </button>
            <button
              onClick={() => setView({ name: 'settings' })}
              className="btn-icon"
              title="Settings"
            >
              <SettingsIcon size={17} />
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.id);
          return (
            <button
              key={item.id}
              onClick={() => setView({ name: item.id })}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
            >
              <Icon size={22} strokeWidth={active ? 2.2 : 1.7} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
